import { MessageBundle } from '@amzn/arb-tools'
import { t } from '@src/translationHelper'
import { INotificationData, IStoreNotification } from '../types'
import { DAYS_AGO, HOURS_AGO, JUST_NOW, MINUTES_AGO, YESTERDAY } from '../constants'

/**
 * Formats a timestamp into a human-readable relative time string.
 *
 * Not a component, so the message bundle has to be passed in by the caller —
 * the plural forms of "N minutes ago" differ by language and cannot be built
 * by concatenating an 's'.
 *
 * @param timestamp - The date to format
 * @param bundle - Message bundle the calling component obtained from useBundle
 * @param locale - The locale string for date formatting (e.g., 'en-US', 'fr-FR')
 * @returns Relative time string (e.g., "Just now", "3 hours ago", "Yesterday")
 */
export function formatRelativeTime (timestamp: Date, bundle: MessageBundle, locale: string = 'en-US'): string {
    const now = new Date()
    const diffMs = now.getTime() - timestamp.getTime()
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    // Less than 1 minute
    if (diffMinutes < 1) {
        return t(bundle, JUST_NOW)
    }

    // Less than 60 minutes
    if (diffMinutes < 60) {
        return t(bundle, MINUTES_AGO, { minutes: diffMinutes })
    }

    // Less than 24 hours
    if (diffHours < 24) {
        return t(bundle, HOURS_AGO, { hours: diffHours })
    }

    // Exactly 1 day
    if (diffDays === 1) {
        return t(bundle, YESTERDAY)
    }

    // Less than 7 days
    if (diffDays < 7) {
        return t(bundle, DAYS_AGO, { days: diffDays })
    }

    // 7 days or more - return absolute date
    const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    }
    return timestamp.toLocaleDateString(locale, options)
}

/**
 * Separates notifications into current store and other stores
 * @param data - The complete notification data
 * @param currentStore - The ID of the current store
 * @returns Object with currentStoreNotifications and otherStoreNotifications arrays
 */
export function separateNotificationsByCurrentStore (
  data: INotificationData,
  currentStore: string,
): {
    currentStoreNotifications: IStoreNotification[];
    otherStoreNotifications: IStoreNotification[];
} {
    const currentStoreNotifications: IStoreNotification[] = []
    const otherStoreNotifications: IStoreNotification[] = []

    data.notifications.forEach((storeNotification) => {
        if (storeNotification.storeId === currentStore) {
            currentStoreNotifications.push(storeNotification)
        } else {
            otherStoreNotifications.push(storeNotification)
        }
    })

    return {
        currentStoreNotifications,
        otherStoreNotifications,
    }
}

/**
 * Filters notification data by multiple selected store IDs
 * @param data - The complete notification data
 * @param selectedStoreIds - Array of store IDs to filter by; empty array means no filter (return all)
 * @returns Filtered notification data containing only the selected stores
 */
export function filterNotificationsByStores (
    data: INotificationData,
    selectedStoreIds: string[],
): INotificationData {
    // If no stores selected, return all notifications (no filter active)
    if (selectedStoreIds.length === 0) {
        return { notifications: data.notifications }
    }

    // Filter to only the selected stores
    const filteredNotifications = data.notifications.filter(
        (storeNotification) => selectedStoreIds.includes(storeNotification.storeId),
    )

    return {
        notifications: filteredNotifications,
    }
}

/**
 * Calculates the total number of individual notifications for a specific store.
 * @param data - The complete notification data
 * @param storeId - The store ID to count notifications for
 * @returns The total count of individual notifications across all tools for the store
 */
export function calculateNotificationCount (data: INotificationData, storeId: string): number {
    const storeNotification = data.notifications.find(
        (notification) => notification.storeId === storeId,
    )
    if (!storeNotification) {
        return 0
    }
    return storeNotification.toolNotifications.reduce(
        (sum, tool) => sum + tool.allIndividualNotifications.length,
        0,
    )
}

/**
 * Calculates the total number of pending tasks across all stores.
 * Uses aggregated notification counts to match what the action center displays.
 * @param data - The complete notification data
 * @returns The aggregate count of pending tasks across all stores and tools
 */
export function calculateTotalNotificationCount (data: INotificationData): number {
    return data.notifications.reduce(
        (total, store) => total + store.toolNotifications.reduce(
            (sum, tool) => sum + tool.aggregatedNotification.numPendingTasks, 0,
        ), 0,
    )
}
